import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PawPrint, CheckCircle2, Stethoscope, Salad, Syringe, Bug, Bone, Brain } from "lucide-react";
import SiteHeader from "../components/SiteHeader";
import Footer from "../components/Footer";
import AppointmentBanner from "../components/AppointmentBanner";
import Testimonials from "../components/home/Testimonials";
import Reveal from "../components/Reveal";

const included = [
  { icon: Stethoscope, title: "Physical Examination", text: "The physical examination is an essential part of the veterinary visit that often shapes the differential diagnosis and guides the diagnostic plan." },
  { icon: Salad, title: "Nutritional Assessment", text: "Based on visual observations such as Body Condition Score (BCS) and Muscle Condition Score (MCS), plus measurements like weight and blood constituents. The MCS is especially important when starting a weight-loss diet, as dramatic losses could be due to a drop in muscle mass." },
  { icon: Syringe, title: "Vaccination Review", text: "Protect your pets by keeping their vaccinations up to date. Unvaccinated pets are at risk from infectious diseases, some of which can transmit to humans. Dogs and cats should receive a booster every 12 months — the first annual vaccine being particularly important." },
  { icon: Bug, title: "Parasite Control", text: "A whole range of parasites can live inside or on our pets, affecting their health and wellbeing — and some pose a risk to people too. As responsible owners we have a duty to proactively protect our pets and families." },
  { icon: Bone, title: "Dental Health Check", text: "Dental disease is very common in cats and dogs. After the age of three, around seven in ten pets have some kind of tooth disorder. Left unattended these can cause irreversible damage to teeth, gums and jawbones." },
  { icon: Brain, title: "Behaviour & Lifestyle Consultation", text: "Expert support for dogs and cats experiencing behavioural challenges affecting their wellbeing or home life. We offer behavioural support directly, and refer to accredited animal behaviourists when additional expertise is required." },
];

const inputCls =
  "w-full rounded-xl border border-pawelle-ink/12 bg-white px-4 py-3.5 font-body text-[15px] text-pawelle-ink outline-none transition focus:border-pawelle-primary placeholder-pawelle-body/70";

const DetailService = () => {
  const [form, setForm] = useState({ name: "", email: "", date: "", time: "", message: "" });
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const submit = (e) => { e.preventDefault(); setSent(true); setTimeout(() => setSent(false), 4000); };

  useEffect(() => { document.title = "Wellness Check – Moggies & Doggies Veterinary Care"; }, []);

  return (
    <div className="min-h-screen bg-white font-body text-pawelle-body">
      <SiteHeader />
      <main>
        {/* Hero */}
        <section className="relative overflow-hidden rounded-b-[40px] bg-pawelle-bg pt-36 pb-16 md:pt-44">
          <PawPrint className="pointer-events-none absolute left-10 top-28 h-20 w-20 -rotate-12 text-white opacity-70" />
          <PawPrint className="pointer-events-none absolute right-16 bottom-10 h-16 w-16 text-white opacity-70" />
          <div className="relative mx-auto max-w-[1290px] px-5 text-center">
            <Reveal animation="fadeInDown">
              <h1 className="font-display text-[40px] font-bold uppercase leading-tight text-pawelle-ink md:text-[60px]">Wellness Check</h1>
            </Reveal>
            <Reveal animation="fadeInUp" delay={120}>
              <h3 className="mx-auto mt-4 max-w-2xl font-display text-[19px] font-medium text-pawelle-ink/80 md:text-[24px]">
                Comprehensive Pet Wellness Check for Lifelong Health
              </h3>
            </Reveal>
            <Reveal animation="fadeInUp" delay={200}>
              <Link to="/gb/en/appointment.html" className="mt-7 inline-flex rounded-full bg-pawelle-primary px-8 py-4 font-display text-[16px] font-medium text-white transition-all duration-300 hover:bg-pawelle-primary-dark hover:-translate-y-0.5">
                Get Started
              </Link>
            </Reveal>
          </div>
        </section>

        {/* Image + description */}
        <section className="mx-auto max-w-[1290px] px-5 py-16 md:py-24">
          <Reveal animation="fadeInUp">
            <img src="/pawelle/img_17-1.jpg" alt="Wellness check" className="w-full rounded-[28px] object-cover shadow-[0_24px_60px_rgba(67,48,109,0.12)]" />
          </Reveal>
          <div className="mt-12 grid gap-12 lg:grid-cols-2">
            <div>
              <h2 className="font-display text-[30px] font-bold text-pawelle-ink md:text-[36px]">Descriptions</h2>
              <p className="mt-5 font-body text-[16px] leading-relaxed text-pawelle-body">
                Wellness screening gives vets and vet nurses the opportunity to assess dogs for any potential or
                underlying health issues. Early detection means they can start treatment sooner to prevent or slow
                disease progression — helping our pets live longer, healthier and happier lives.
              </p>
              <p className="mt-4 font-body text-[16px] leading-relaxed text-pawelle-body">
                Examples of diseases that can be detected at a wellness screening appointment include liver and kidney
                disease, diabetes and high blood pressure.
              </p>
            </div>
            <Reveal animation="fadeInRight">
              <img src="/pawelle/img_6.jpg" alt="Pet care detail" className="h-full w-full rounded-[28px] object-cover shadow-[0_24px_60px_rgba(67,48,109,0.12)]" />
            </Reveal>
          </div>
        </section>

        {/* What's included */}
        <section className="bg-pawelle-bg py-20 md:py-28">
          <div className="mx-auto max-w-[1290px] px-5">
            <Reveal animation="fadeInUp">
              <h2 className="max-w-2xl font-display text-[28px] font-bold text-pawelle-ink md:text-[34px]">
                Complete care for your pet. Complete peace of mind for you.
              </h2>
              <p className="mt-4 max-w-3xl font-body text-[16px] leading-relaxed text-pawelle-body">
                From vaccinations to face-to-face health checks and our vet helpline — whether you own a dog, cat or
                rabbit, our Complete Care Plan provides your pet with cost-effective preventative care for every life
                stage.
              </p>
              <h3 className="mt-8 font-display text-[22px] font-semibold text-pawelle-ink">
                What's Included in Our Wellness Check:
              </h3>
            </Reveal>
            <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {included.map((it, i) => {
                const Icon = it.icon;
                return (
                  <Reveal key={it.title} animation="fadeInUp" delay={i * 70} className="rounded-[24px] bg-white p-7 shadow-[0_14px_40px_rgba(67,48,109,0.06)]">
                    <div className="flex h-14 w-14 items-center justify-center rounded-full bg-pawelle-bg text-pawelle-primary">
                      <Icon className="h-7 w-7" />
                    </div>
                    <h3 className="mt-5 font-display text-[19px] font-semibold text-pawelle-ink">{it.title}</h3>
                    <p className="mt-2 font-body text-[15px] leading-relaxed text-pawelle-body">
                      {it.text}
                    </p>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </section>

        {/* Booking form */}
        <section className="mx-auto max-w-[900px] px-5 py-20 md:py-28">
          <div className="text-center">
            <h2 className="font-display text-[28px] font-bold text-pawelle-ink md:text-[34px]">Schedule Your Pet's Wellness Check Today</h2>
            <p className="mt-3 font-body text-[16px] text-pawelle-body">
              Keep your companion healthy, happy, and thriving with Moggies & Doggies' trusted care.
            </p>
          </div>
          <Reveal animation="fadeInUp" className="mt-10 rounded-[28px] bg-pawelle-bg p-6 md:p-10">
            <form onSubmit={submit} className="grid gap-5 sm:grid-cols-2">
              <input className={inputCls} placeholder="Name" value={form.name} onChange={set("name")} required />
              <input className={inputCls} type="email" placeholder="Email" value={form.email} onChange={set("email")} required />
              <input className={inputCls} type="date" value={form.date} onChange={set("date")} />
              <input className={inputCls} type="time" value={form.time} onChange={set("time")} />
              <textarea className={`${inputCls} sm:col-span-2 min-h-[110px] resize-y`} placeholder="Message" value={form.message} onChange={set("message")} />
              <button type="submit" className="sm:col-span-2 rounded-xl bg-pawelle-primary px-6 py-4 font-display text-[16px] font-medium text-white transition-all duration-300 hover:bg-pawelle-primary-dark">
                {sent ? "Appointment Booked!" : "Book Appointment"}
              </button>
            </form>
          </Reveal>
        </section>

        <div className="mx-auto max-w-[1290px] px-5">
          <AppointmentBanner />
        </div>
        <Testimonials />
      </main>
      <Footer />
    </div>
  );
};

export default DetailService;
